import React, {useState, useEffect} from 'react';

import _ from 'lodash';
import {useField, ErrorMessage} from 'formik';
import {useTranslation} from 'react-i18next';

import AsyncSelect from 'react-select/async';
import {FormErrorMessage} from '../../../../form-features/FormErrorMessage';
import {SelectStructure} from '../SelectStructure';

const rootElement = document.getElementById('root');
const defaultMenuProps = {
    menuPortalTarget: rootElement,
    menuPosition: 'fixed',
    maxMenuHeight: 300,
};
export function FormikAsyncSelect({
    loadOptions,
    loadSingleOption,
    fieldName,
    components,
    reloadProp,
    disabled,
    onChange,
    isClearable,
    placeholder,
    label,
    prefix,
    isMulti,
    menuHeight,
    unsetPortal,
}) {
    const menuProps = unsetPortal
        ? {}
        : {...defaultMenuProps, maxMenuHeight: menuHeight || defaultMenuProps.maxMenuHeight};

    let [, fieldMeta, fieldHelpers] = useField(fieldName);

    const {t} = useTranslation();

    // if there is already a value, and we have a way to load a single one, then we should load the
    // current selection before actually rendering
    let [loading, setLoading] = useState(fieldMeta.value && _.isFunction(loadSingleOption));
    let [initialValue, setInitialValue] = useState(null);

    useEffect(() => {
        if (fieldMeta.value && _.isFunction(loadSingleOption)) {
            loadSingleOption(fieldMeta.value).then((opt) => {
                setInitialValue(opt);
                setLoading(false);
            });
        }
        if (!fieldMeta.value || disabled) {
            setInitialValue(null);
        }
    }, [fieldMeta.value, setLoading, disabled]);

    const optionChosen = (val) => {
        fieldHelpers.setValue(val?.value ?? '');
        fieldHelpers.setTouched(true, false);
        setInitialValue(val ?? '');
        if (_.isFunction(onChange)) {
            onChange(val?.value ?? '');
        }
    };

    // if we are loading the currently-selected value, then show an empty state
    if (loading)
        return (
            <SelectStructure
                label={label}
                prefix={prefix}
                disabled={disabled}
                errorMessage={<ErrorMessage name={fieldName} component={FormErrorMessage} />}>
                <div>
                    <AsyncSelect
                        disabled
                        classNamePrefix="dropdown-select"
                        value={{value: fieldMeta.value, label: '...'}}
                        placeholder={placeholder ?? t('searchableDropdown.placeholder')}
                        menuPosition={'fixed'}
                        {...menuProps}
                        styles={{
                            menu: (base) => ({
                                ...base,
                                zIndex: 9999,
                                position: 'absolute',
                                overflow: 'scroll',
                                marginTop: '1px',
                                height: 'auto',
                            }),
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </div>
            </SelectStructure>
        );

    return (
        <SelectStructure
            label={label}
            prefix={prefix}
            disabled={disabled}
            errorMessage={<ErrorMessage name={fieldName} component={FormErrorMessage} />}>
            <div>
                <AsyncSelect
                    key={JSON.stringify(reloadProp)}
                    defaultOptions
                    classNamePrefix="dropdown-select"
                    value={initialValue ?? null}
                    loadOptions={loadOptions}
                    onChange={optionChosen}
                    isClearable={isClearable !== false}
                    placeholder={placeholder ?? t('searchableDropdown.placeholder')}
                    components={components}
                    isDisabled={disabled}
                    isMulti={isMulti}
                    menuPosition={'fixed'}
                    {...menuProps}
                    styles={{
                        menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                            position: 'absolute',
                            overflow: 'scroll',
                            marginTop: '1px',
                            height: 'auto',
                        }),
                        menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                        }),
                    }}
                />
            </div>
        </SelectStructure>
    );
}
